import type { ReactElement } from "react";
import { BAG_SORT_ORDERS, type BagSortOrder } from "odyssey-shared";
import { Menu, type MenuEntry } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";

/**
 * The Inventory window's Sort menu (P2.8-T48, ADR-125 decisions 3.6 and 4.2; the design's `InvTools` in the Inventory
 * card): the kit's `Menu` headed "Sort bags by", 104 wide, with Type, Rarity, Name and Newest first, the last order the
 * page sent this session checked (the kit's `checked`: a `menuitemradio` leading with the 8 px check), none before the
 * first. Choosing one asks the bag store to sort, which sends nothing for a bag already in that order, except Newest
 * first, whose order only the server can read. The tool row's Sort button opens it under the row and reads the store's
 * `sort.pending` for its busy look.
 */

/**
 * The menu's least width in logical pixels, the design's.
 */
export const SORT_MENU_WIDTH = 104;

/**
 * What a screen reader reads for the Sort button while a sort waits and for the spinner once it is slow; nothing
 * shows it (ADR-125 decision 11.1: the spinner, not the design's sentence).
 *
 * @param by The order waiting.
 * @returns The words.
 * @example
 * sortingNote("rarity");
 * // "Sorting by rarity: waiting for the server."
 */
export function sortingNote(by: BagSortOrder): string {
  return say(`bag.sort.waiting.${by}`);
}

/**
 * The menu's entries: each order in the design's order, the last sent checked.
 *
 * @param last The last order sent this session, or null.
 * @returns The entries.
 * @example
 * sortMenuEntries("name").map((entry) => entry.checked);
 * // [false, false, true, false]
 */
export function sortMenuEntries(last: BagSortOrder | null): MenuEntry<BagSortOrder>[] {
  return BAG_SORT_ORDERS.map((by) => ({ value: by, label: say(`bag.sort.orders.${by}`), checked: by === last }));
}

/**
 * The open Sort menu. It holds the keyboard while open, as every kit menu does, and closes on Escape, on Tab and on a
 * choice; the tool row places it and closes it on a press outside.
 *
 * @param props The last order sent (`last`), `onSelect` with the order chosen, and `onClose`.
 * @returns The menu.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function BagSortMenu({ last, onSelect, onClose }: { readonly last: BagSortOrder | null; readonly onSelect: (by: BagSortOrder) => void; readonly onClose: () => void }): ReactElement {
  return (
    <div className="ot-bag__sort-menu" data-bag="sort-menu">
      <Menu label={say("bag.sort.heading")} heading={say("bag.sort.heading")} minWidth={SORT_MENU_WIDTH} items={sortMenuEntries(last)} onSelect={onSelect} onClose={onClose} />
    </div>
  );
}
